
import React, { useMemo, useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Offcanvas } from "react-bootstrap";
import ProjectCard from "../Projects/ProjectCards";

import creditrisk from "../../assets/Projects/creditrisk.png";
import facemask from "../../assets/Projects/facemask.png";
import ner from "../../assets/Projects/ner.png";
import trainer from "../../assets/Projects/trainer24.png";
import igtime from "../../assets/Projects/igtime.png";
import volsur from "../../assets/Projects/volsur.png";
import credit_rating from "../../assets/Projects/credit_rating.png";

// --- helpers ---
function getYearFromDate(dateStr) {
  // expects "MM-YYYY" (like "11-2025")
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  const year = parts[1];
  return year || "";
}

function normalizeType(type) {
  return String(type || "").trim();
}

export default function Projects() {
  // Scroll to top on mount
  useEffect(() => {
  window.scrollTo({ top: 0, behavior: "instant" });
}, []);

  // 1) Put all projects in one array (easier to filter + maintain)
  const projects = useMemo(
    () => [
      {
        imgPath: credit_rating,
        title: (
          <>
            Corporate Credit Rating on
            <br />
            Automotive Industries
          </>
        ),
        description:
          "Designed a credit risk scorecard for global automobile manufacturers by selecting key financial and industry-specific indicators, and benchmarking firms through peer and rating validation analysis.",
        link: "pre2.pdf",
        date: "11-2025",
        type: "Risk",
      },
      {
        imgPath: volsur,
        title: "Volatility Surfaces & Interpolation",
        description:
          "This mini project is aimed to plot the volatility surface by computing implied volatilities via Brent's method and applying cubic spline interpolation for smooth modeling.",
        link: "https://colab.research.google.com/drive/106MHMgC_ykDT4BQLykJ8tmU5Y7D-V4Q9",
        date: "08-2025",
        type: "Quant",
      },
      {
        imgPath: creditrisk,
        title: (
          <>
            Home Credit -
            <br />
            Credit Risk Model Stability
          </>
        ),
        description:
          "Trained and hypertuned a model (LightGBM) to predict which clients are more likely to default on their loans.",
        link: "https://www.kaggle.com/code/daviddirethucus/home-credit-risk-lightgbm",
        date: "03-2024",
        type: "Risk",
      },
      {
        imgPath: facemask,
        title: "Face Mask Inpainting",
        description:
          "The model is designed to remove the face-mask from facial image and inpaint the left-behind region based on a customized GAN-network approach.",
        link: "https://github.com/davidmilsky/Face-Mask_Inpainting",
        date: "09-2021",
        type: "Machine Learning",
      },
      {
        imgPath: ner,
        title: "Named Entity Recognition (NER)",
        description:
          "The NER models is mainly trained using a popular NLP library Spacy to detect both Engligh and Chinese entities.",
        link: "https://github.com/davidmilsky/NER_engxchin?tab=readme-ov-file",
        date: "09-2023",
        type: "Machine Learning",
      },
      {
        imgPath: trainer,
        title: "trAlner24",
        description:
          "This group project aims to detect the users' motion when doing exercises (squat & planking), help them count or time the exercise, and tell them whether their exercise forms are correct or not.",
        link: "https://github.com/eddylamhw/trAIner24",
        date: "01-2021",
        type: "Machine Learning",
      },
      {
        imgPath: igtime,
        title: (
          <>
            (Research)
            <br />
            The potential factors that affect the time spent for users on Instagram
          </>
        ),
        description:
          "explore the relationship of features and predict the time spent on Instagram by comparing XGBoost and Random Forest Regression models and determine the feature importance using SHAP values.",
        link: "https://github.com/eddylamhw/trAIner24",
        date: "02-2022",
        type: "Data Science",
      },
    ],
    []
  );

  // 2) Build filter options dynamically from the data
  const allTypes = useMemo(() => {
    const set = new Set(projects.map((p) => normalizeType(p.type)).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [projects]);

  const allYears = useMemo(() => {
    const set = new Set(projects.map((p) => getYearFromDate(p.date)).filter(Boolean));
    // sort years descending
    return ["All", ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
  }, [projects]);

  // 3) Filter state
  const [typeFilter, setTypeFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("All");

  // mobile offcanvas state
  const [showFilters, setShowFilters] = useState(false);

  // 4) Apply filtering
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const pType = normalizeType(p.type);
      const pYear = getYearFromDate(p.date);

      const typeOk = typeFilter === "All" || pType === typeFilter;
      const yearOk = yearFilter === "All" || pYear === yearFilter;

      return typeOk && yearOk;
    });
  }, [projects, typeFilter, yearFilter]);

  const clearFilters = () => {
    setTypeFilter("All");
    setYearFilter("All");
  };

  const FilterControls = ({ compact = false }) => (
    <div className={`project-filters ${compact ? "project-filters--compact" : ""}`}>
      <Form.Group className="project-filters__group">
        <Form.Label className="project-filters__label">Type</Form.Label>
        <Form.Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="project-filters__select"
          aria-label="Filter by type"
        >
          {allTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="project-filters__group">
        <Form.Label className="project-filters__label">Year</Form.Label>
        <Form.Select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="project-filters__select"
          aria-label="Filter by year"
        >
          {allYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <div className="project-filters__actions">
        <Button variant="outline-light" onClick={clearFilters} className="project-filters__btn">
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <Container fluid className="project-section">
      <Container>
        <div className="projects-header">
          <h1 className="project-heading" style={{ textAlign: "left" }}>
            All <strong className="purple">Projects</strong>
          </h1>

          {/* Desktop / tablet: inline filters */}
          <div className="d-none d-md-block">
            <FilterControls />
          </div>

          {/* Mobile: button opens offcanvas */}
          <div className="d-md-none mt-3">
            <Button
              variant="outline-light"
              className="w-100"
              onClick={() => setShowFilters(true)}
            >
              Filter Projects
            </Button>

            <Offcanvas
              show={showFilters}
              onHide={() => setShowFilters(false)}
              placement="bottom"
              className="project-filters-canvas"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>Filters</Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <FilterControls compact />
                <Button
                  variant="light"
                  className="w-100 mt-3"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </Offcanvas.Body>
            </Offcanvas>
          </div>
        </div>

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {filteredProjects.length === 0 ? (
            <div className="text-center mt-4" style={{ opacity: 0.85 }}>
              No projects match your filters.
            </div>
          ) : (
            filteredProjects.map((p, idx) => (
              <Col key={`${p.type}-${p.date}-${idx}`} md={4} className="project-card">
                <ProjectCard
                  imgPath={p.imgPath}
                  title={p.title}
                  description={p.description}
                  link={p.link}
                  date={p.date}
                  type={p.type}
                />
              </Col>
            ))
          )}
        </Row>
      </Container>
    </Container>
  );
}
